/**
 * TestIt schemas (BL-214 · W3.8 · F3).
 *
 * ─── Why this file exists ─────────────────────────────────────────────────
 *
 * `POST /heroes/testit/vetting/:talentId/verdict` shipped with an INLINE
 * TypeScript type on its `@Body()` parameter:
 *
 *   dto: { verdict: 'passed' | 'declined'; invitationId?: string | null; … }
 *
 * A TS type is erased at runtime. Nest's global `ValidationPipe` validates
 * against a DTO **class**'s metadata, and an inline type produces none — so the
 * pipe saw `Object`, had nothing to check, and passed the body straight
 * through. `verdict` is a `varchar(16)`, so any string up to 16 characters
 * persisted: `"maybe"`, `"PASSED"`, `""`. The compiler said the endpoint was
 * typed; the database disagreed.
 *
 * The vocabulary is therefore enforced HERE, in code, by a zod schema the
 * controller mounts with `zodPipe`. That is the whole reason migration 0120
 * ships no CHECK constraint: a CHECK would enforce the rule for the new image
 * and abort the deploy for the old one, while this fixes the thing that was
 * actually broken.
 */

import { z } from 'zod';

/**
 * Body of "record (or replace) a Pass/Decline for a talent".
 *
 * STRICT on purpose. The previous contract accepted a client-supplied
 * `invitationId` that no client ever sent and the service never keyed on — and
 * an id the CALLER chooses is the same shape of defect as the missing pipeline
 * check: it lets a request aim a decision at a row the caller did not earn.
 * The engagement is now resolved server-side from the caller's own pipeline, so
 * an `invitationId` in the body is a stale client, and a 400 says so instead of
 * being silently dropped.
 *
 * There is no `snapshot` any more (W5.8 · H20). The evidence a decision rests
 * on used to be whatever the BROWSER sent — unvalidated, and inherited in place
 * by the next verdict when a Decline arrived with no body. The server now
 * captures it, in the verdict's own transaction, from the canonical readers;
 * a client that still sends one is stale, and strict parsing says so (400)
 * rather than dropping it.
 */
export const recordTestitVerdictSchema = z.strictObject({
  /** The relationship the decision is about — the drawer's journey (X8). */
  journeyId: z.uuid().optional(),
  verdict: z.enum(['passed', 'declined']),
});

export type RecordTestitVerdictInput = z.infer<typeof recordTestitVerdictSchema>;
